import { useEffect, useRef } from "react";
import { adminSocket } from "@/lib/admin-socket";
import { useNotificationSummary } from "@/hooks/useNotificationSummary";
import { playUrgentChime } from "@/components/queries/ringing";

/**
 * Keeps the admin socket connected while logged in and refreshes notification
 * badges live when ticket / handoff events arrive.
 */
export function NotificationRealtimeBridge() {
  const { refresh, preferences } = useNotificationSummary();
  const soundRef = useRef(preferences.soundOnUrgent);
  soundRef.current = preferences.soundOnUrgent;
  const lastChimeAt = useRef(0);

  useEffect(() => {
    let mounted = true;

    const maybeChime = (priority?: string, reason?: string) => {
      if (!soundRef.current) return;
      if (priority !== "urgent" && reason !== "lead_handoff" && reason !== "ticket-created") {
        return;
      }
      // Queries page owns the full handoff ring when Ready to chat is on.
      if (reason === "lead_handoff" && window.location.pathname.startsWith("/queries")) {
        return;
      }
      const now = Date.now();
      if (now - lastChimeAt.current < 2500) return;
      lastChimeAt.current = now;
      playUrgentChime();
    };

    const onRefresh = (data: {
      reason: string;
      category?: string;
      priority?: string;
    }) => {
      if (!mounted) return;
      refresh();
      maybeChime(data.priority, data.reason);
    };

    const onTicketCreated = () => {
      if (!mounted) return;
      refresh();
      maybeChime("urgent", "ticket-created");
    };

    const onTicketUpdated = () => {
      if (!mounted) return;
      refresh();
    };

    void adminSocket.connect().catch(() => {
      /* REST still works; badges poll */
    });

    adminSocket.onAdminNotificationsRefresh(onRefresh);
    adminSocket.onTicketCreated(onTicketCreated);
    adminSocket.onTicketUpdated(onTicketUpdated);

    return () => {
      mounted = false;
      adminSocket.offAdminNotificationsRefresh(onRefresh);
      adminSocket.offTicketCreated(onTicketCreated);
      adminSocket.offTicketUpdated(onTicketUpdated);
    };
  }, [refresh]);

  return null;
}
